import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "em_cart_v1";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch {}
    }, [items]);

    const addItem = useCallback((product, { size = "M", quantity = 1 } = {}) => {
        if (product?.coming_soon) {
            // Prevent adding pre-release items
            return;
        }
        setItems((prev) => {
            const key = `${product.id}|${size}`;
            const idx = prev.findIndex((i) => `${i.product_id}|${i.size}` === key);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
                return copy;
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    name: product.name,
                    price: product.price,
                    image_url: product.image_url,
                    size,
                    quantity,
                },
            ];
        });
    }, []);

    const removeItem = useCallback((product_id, size) => {
        setItems((prev) =>
            prev.filter((i) => !(i.product_id === product_id && i.size === size))
        );
    }, []);

    const updateQty = useCallback((product_id, size, quantity) => {
        setItems((prev) =>
            prev
                .map((i) =>
                    i.product_id === product_id && i.size === size
                        ? { ...i, quantity: Math.max(1, quantity) }
                        : i
                )
                .filter((i) => i.quantity > 0)
        );
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const total = useMemo(
        () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
        [items]
    );
    const count = useMemo(
        () => items.reduce((n, i) => n + i.quantity, 0),
        [items]
    );

    const value = {
        items,
        total,
        count,
        open,
        setOpen,
        addItem,
        removeItem,
        updateQty,
        clear,
    };

    return (
        <CartContext.Provider value={value}>{children}</CartContext.Provider>
    );
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used inside CartProvider");
    return ctx;
}
